import { roundNoFixed } from 'components/utils';
import { unit } from 'mathjs';
import { doc, getDoc } from 'firebase/firestore';

export const generateInventorySheetRDM = async (worksheet, lastOps, tankDataMap, volumeUnit, extraOperationFields, t, cellarsRef) => {
  // Define styles
  const boldStyle = {
    font: { bold: true, name: 'Arial' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  };

  const borderedStyle = {
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  };

  const grayBackgroundStyle = {
    fill: {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' } // Light gray color
    }
  };

  const boldCenteredStyle = {
    font: { bold: true, name: 'Arial' },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  };

  const titleRow = worksheet.addRow(['', 'Distillerie Rivière du mat', '', 'ENREGISTREMENT', '', 'ENR 032']);
  const subtitleRow = worksheet.addRow(['', '', '', 'Inventaire journalier des stocks alcools', '', 'version 2']);
  worksheet.addRow(['', '', '', '', '', 'Date modication 10/05/2021']);
  worksheet.addRow([]);
  worksheet.addRow(['', '', '', '', '', 'page : 1/1']);
  worksheet.addRow([]);
  worksheet.addRow(['', 'Date :', new Date().toLocaleDateString('fr-FR'), '', '', '', '']);
  const warningRow = worksheet.addRow(['', 'ATTENTION : LES CUVES VIDES NE SONT PAS DEGAZEES', '', '', '', '', '', '']);
 
  worksheet.mergeCells('B1:C5');
  const titleCell = titleRow.getCell(2);
  titleCell.style = {
    font: { bold: true, name: 'Arial' },
    alignment: { horizontal: 'center', vertical: 'middle' }
  };
  // Apply borders to the entire merged range
  for (let row = 1; row <= 5; row++) {
    for (let col = 2; col <= 3; col++) {
      const cell = worksheet.getCell(row, col);
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    }
  }

  worksheet.mergeCells('D1:E1');
  const enregistrementCell = titleRow.getCell(4);
  enregistrementCell.style = {
    font: { bold: true, name: 'Arial' },
    alignment: { horizontal: 'center', vertical: 'middle' }
  };
  // Apply borders to the entire merged range
  for (let col = 4; col <= 5; col++) {
    const cell = worksheet.getCell(1, col);
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  }

  worksheet.mergeCells('D2:E5');
  const subtitleCell = subtitleRow.getCell(4);
  subtitleCell.style = {
    font: { bold: true, name: 'Arial', size: 12 },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true }
  };
  // Apply borders to the entire merged range
  for (let row = 2; row <= 5; row++) {
    for (let col = 4; col <= 5; col++) {
      const cell = worksheet.getCell(row, col);
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    }
  }

  worksheet.mergeCells('B8:G8');
  const warningCell = warningRow.getCell(2);
  warningCell.style = {
    ...boldCenteredStyle
  };
  // Apply borders to the entire merged range
  for (let col = 2; col <= 7; col++) {
    const cell = worksheet.getCell(8, col);
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  }

  // Apply borderedStyle to cells F1 through F5
  for (let row = 1; row <= 5; row++) {
    const cell = worksheet.getCell(row, 6); // Column F is index 6
    cell.style = {
      ...cell.style,
      ...borderedStyle
    };
  }

  const headerRow = worksheet.addRow([
    '',
    'N° Citerne',
    'Volume en L',
    'Degré',
    'Alcool pur en lap',
    'Nature des produits',
    'Capacité utile en L',
    'CUVE DISPONIBLE'
  ]);
  // Set header row height
  headerRow.height = 40;

  // Apply bold style and word wrap to all header cells
  headerRow.eachCell((cell) => {
    if (cell.value) {
      cell.style = {
        ...boldStyle,
        alignment: { vertical: 'middle', horizontal: 'center', wrapText: true }
      };
    }
  });

  // Set column widths
  worksheet.columns = [
    { width: 18 },
    { width: 15 },
    { width: 18 },
    { width: 12 },
    { width: 18 },
    { width: 24 },
    { width: 18 },
    { width: 18 }
  ];

  const dataPromises = Object.entries(tankDataMap).map(async ([tankId, tankData]) => {
    const operation = lastOps.find(op => op.tankNumber === tankId);
    const { compartment } = tankData.fullActiveCert || {};
    const maxVolume = compartment ? roundNoFixed(unit(compartment.maxVolume, 'L').toNumber(volumeUnit), 4) : '';
    
    let cellarName = '';
    let volume = 'VIDE';
    let abv = '';
    let pureAlcohol = '';
    let productType = '';
    let availability = '';
    
    if (operation) {
      cellarName = operation.cellarName || operation.cellar;
      const height = operation.form.results.height?.value || operation.form.results.height;
      const realVolume = operation.form.results.volume?.value || operation.form.results.volume;
      volume = (height === '0' || realVolume === '0' || realVolume === 0) ? 'VIDE' 
        : (height === '0' && realVolume !== '0') ? `0 ↔ ${realVolume}` 
          : realVolume ? parseFloat(realVolume) : '';
      
      const rawAbv = operation.form.paConversionResults?.abvAt20c?.value || operation.form.results.volumeConvResult?.abvAt20c?.value || '';
      abv = rawAbv ? parseFloat(rawAbv) : '';
      
      const rawPureAlcohol = operation.form.paConversionResults?.pureAlcoholVolumeAt20c?.value || operation.form.results.volumeConvResult?.pureAlcoholVolumeAt20c?.value || '';
      pureAlcohol = rawPureAlcohol ? parseFloat(rawPureAlcohol) : '';
      
      productType = operation.form.extraFields?.find(f => f.name === 'nature-des-produits')?.value || '';
      availability = operation.form.extraFields?.find(f => f.name === 'cuve-disponible')?.value || '';
    } else {
      const cellarRef = doc(cellarsRef, tankData.cellar);
      const cellarDoc = await getDoc(cellarRef);
      cellarName = cellarDoc.exists() ? cellarDoc.data().name : '';
    }
    
    const parsedMaxVolume = maxVolume ? parseFloat(maxVolume) : '';

    return {
      cellar: tankData.cellar,
      cellarName,
      row: [
        cellarName,
        tankData.name || tankId,
        volume,
        abv,
        pureAlcohol,
        productType,
        parsedMaxVolume,
        availability
      ]
    };
  });

  const data = await Promise.all(dataPromises);
  
  // Group tanks by cellar
  const tanksByCellar = data.reduce((acc, tank) => {
    if (!acc[tank.cellar]) {
      acc[tank.cellar] = {
        cellarName: tank.cellarName,
        tanks: []
      };
    }
    acc[tank.cellar].tanks.push(tank);
    return acc;
  }, {});

  // Sort cellars alphabetically by name
  const sortedCellars = Object.entries(tanksByCellar)
    .sort(([_, a], [__, b]) => a.cellarName.localeCompare(b.cellarName));

  // Process each cellar group
  let totalPureAlcohol = 0;
  let cellarStartRow = 10;
  
  for (const [cellarId, cellarData] of sortedCellars) {
    const { cellarName, tanks } = cellarData;
    let subtotal = ['', 'Sous-total', '', '', 0, '', '', ''];
    let currentGroupSize = 0;

    // Process each tank in the cellar
    for (const tank of tanks) {
      const worksheetRow = worksheet.addRow(tank.row);
      worksheetRow.getCell(1).style = boldCenteredStyle;
      // Add borders to all cells in the data row
      worksheetRow.eachCell((cell) => {
        cell.style = {
          ...cell.style,
          ...borderedStyle
        };
      });
      currentGroupSize++;
      
      const pureAlcoholValue = parseFloat(tank.row[4]);
      if (!isNaN(pureAlcoholValue)) {
        subtotal[4] = roundNoFixed(subtotal[4] + pureAlcoholValue, 4);
        totalPureAlcohol = roundNoFixed(totalPureAlcohol + pureAlcoholValue, 4);
      }
    }

    // Merge and style the cellar name cells
    worksheet.mergeCells(`A${cellarStartRow}:A${cellarStartRow + currentGroupSize - 1}`);
    const cellarCell = worksheet.getCell(cellarStartRow, 1);
    cellarCell.style = {
      ...boldCenteredStyle,
      ...borderedStyle,
      ...grayBackgroundStyle,
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true }
    };
    
    // Add subtotal row
    const subtotalRow = worksheet.addRow(subtotal);
    subtotalRow.getCell(2).style = boldCenteredStyle;
    subtotalRow.eachCell((cell) => {
      cell.style = {
        ...cell.style,
        ...borderedStyle
      };
    });

    cellarStartRow = cellarStartRow + currentGroupSize + 1;
  }
  
  if (data.length > 0) {
    const totalRow = worksheet.addRow([
      '',
      'Total',
      '',
      '',
      totalPureAlcohol,
      '',
      '',
      ''
    ]);
    totalRow.getCell(2).style = boldCenteredStyle;
    // Add borders to all cells in the total row
    totalRow.eachCell((cell) => {
      cell.style = {
        ...cell.style,
        ...borderedStyle
      };
    });

    // Add borders to the legend rows
    const legendRow1 = worksheet.addRow(['', 'AT: alcool de tetes; RA: rhum agricole; RT: rhum traditionnel; AN: alcool neutre; F: Flegmes. Edv/ Eau de vie de canne;RL:rhum léger', '', '', '', '', '', '']);
    const legendRow2 = worksheet.addRow(['', 'P/D : propre et disponible', '', '', '', '', '', '']);
    
    legendRow1.eachCell((cell) => {
      cell.style = {
        ...cell.style,
        ...borderedStyle
      };
    });
    legendRow2.eachCell((cell) => {
      cell.style = {
        ...cell.style,
        ...borderedStyle
      };
    });
  }

  // Format numeric columns
  [3, 4, 6].forEach(col => {
    worksheet.getColumn(col).numFmt = '0.00';
  });
};
